import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  Box,
  ButtonStyled,
  SpanStyled,
  SubHeadingStyled,
  DataContainerHeader,
  TextStyled,
  CheckList,
  ThemeLoader,
  DurationToggleContainer,
  DurationToggle,
  DurationOption,
} from "../../../components/styled/Elements";
import {
  SettingsWrapper,
  FormDataContainer,
  SubscriptionWrapper,
} from "./styled";
import { useLocation, useNavigate } from "react-router-dom";
import {
  dummyPlansList,
  enterprisePlanCheckList,
} from "../../../helpers/dummyConstants";
import {
  BillingIcon,
  Chat,
  EditIcon,
  PlusIcon,
} from "../../../components/styled/AllImages";
import AddLicenseModal from "./components/AddLicenseModal";
import { useUser } from "../../../context/userContext";
import BillingAPI from "../../../services/api/billingApi";
import { toast } from "react-toastify";

const Billing = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { user, fetchUserInfo } = useUser();
  const navigate = useNavigate();
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [action, setAction] = useState(null);
  const [autoSubscibe, setAutoSubscribe] = useState(1);
  const [planData, setPlanData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddLicenseModal, setShowAddLicenseModal] = useState(false);
  const [showContactSalesModal, setShowContactSalesModal] = useState(false);
  const [plansList, setPlansList] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [modalTitle, setModalTitle] = useState(""); // State for modal title

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get("status");

  const isCustomHighlighted = false;

  const clearParams = () => {
    // Navigate to the same path without search params
    navigate(location.pathname, { replace: true });
  };

  const addLicense = (title, plan) => {
    if (plan && plan?.id == planData?.id && !planData?.expired) {
      setAction("extend");
      setPlanData({
        id: plan?.id,
        amount:
          plan?.plan_prices?.find(
            (price) => price?.duration == selectedDuration
          )?.price || "",
        duration: selectedDuration || 1,
      });
    } else if (
      (plan && plan?.id != planData?.id) ||
      (plan?.id == planData?.id && planData?.expired)
    ) {
      setAction("renew");
      setPlanData({
        id: plan?.id,
        amount:
          plan?.plan_prices?.find(
            (price) => price?.duration == selectedDuration
          )?.price || "",
        duration: selectedDuration || 1,
      });
    } else {
      setAction("extend");
      setPlanData({
        id: user?.organization?.latest_subscription?.plan_id,
        amount: user?.organization?.latest_subscription?.total_amount,
        expired: parseInt(user?.organization?.latest_subscription?.expired),
        type: user?.organization?.latest_subscription?.plan?.type,
      });
    }
    setModalTitle(title); // Set the modal title based on the button clicked
    setShowAddLicenseModal(true); // Show the modal
  };
  const contactSales = () => {
    setShowContactSalesModal(true); // Show the modal
  };

  const getPlansList = async () => {
    try {
      const res = await BillingAPI.getPlansList();
      if (res.code === 200 && res.data) {
        setPlansList(res.data);
      } else {
        setPlansList([]);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getPlansList();
  }, [language]);

  useEffect(() => {
    if (user?.organization) {
      setAutoSubscribe(parseInt(user?.organization?.auto_renewal));
    }
  }, [user]);
  const onStatusToggle = async () => {
    if (!statusUpdateLoading) {
      try {
        setStatusUpdateLoading(true);
        const { data, code, message } = await BillingAPI.autoSubscribeStatus(
          autoSubscibe ? 0 : 1
        );
        if (code === 200 && data) {
          await fetchUserInfo();
          toast.success(
            autoSubscibe
              ? t("auto_renewal_turned_off")
              : t("auto_renewal_turned_on")
          );
        } else {
          toast.error(message);
        }
      } catch (error) {
      } finally {
        setStatusUpdateLoading(false);
      }
    }
  };

  const requestGracePeriod = async () => {
    try {
      const { code, message, data } = await BillingAPI.requestGracePeriod();
      if (code === 200) {
        toast.success(message);
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
    } finally {
    }
  };

  useEffect(() => {
    if (status) {
      if (status === "CAPTURED") {
        fetchUserInfo();
        toast.success(t("payment_successful")); // you can customize this message
        clearParams();
      } else if (status === "DECLINED") {
        toast.error(t("payment_failed")); // you can customize this message
        clearParams();
      } else if (status === "FAILED") {
        toast.error(t("payment_failed_2"));
        clearParams();
      }
    } else fetchUserInfo();
  }, [status]);

  return (
    <SettingsWrapper>
      {/* <Box className="content-head">
        <Box className="w-fit d-flex align-items-center gap-3">
          <Box className="billing-icon-wrapper">
            <BillingIcon />
          </Box>
          <SubHeadingStyled size="16px" weight="700" color="var(--text)">
            {t("billing")}
          </SubHeadingStyled>
        </Box>
      </Box> */}
      <Box className="content-container">
        <Row className="mt-3">
          <Col md={12}>
            <FormDataContainer className="dataContainer">
              {/* <DataContainerHeader
                weight="600"
                label={t("subscription_plan")}
              /> */}
              <SubscriptionWrapper>
                {loading ? (
                  <ThemeLoader />
                ) : (
                  <>
                    <Box>
                      <DurationToggleContainer>
                        <DurationToggle>
                          <DurationOption
                            active={selectedDuration === 1}
                            onClick={() => setSelectedDuration(1)}
                          >
                            {t("monthly")}
                          </DurationOption>
                          <DurationOption
                            active={selectedDuration === 12}
                            onClick={() => setSelectedDuration(12)}
                          >
                            {t("annually")}
                          </DurationOption>
                        </DurationToggle>
                      </DurationToggleContainer>
                    </Box>
                    <Row>
                      {plansList &&
                        plansList.length > 0 &&
                        plansList.map((plan) => {
                          const isExpired = parseInt(
                            user?.organization?.latest_subscription?.expired
                          );
                          const isFree = plan?.category === "free";
                          const isHighlighted =
                            plan?.id ==
                            user?.organization?.latest_subscription?.plan_id;
                          const buttonLabel = isHighlighted
                            ? t("renew_plan")
                            : t("upgrade");

                          const title =
                            language === "ar" ? plan.title_ar : plan.title_en;
                          const description =
                            language === "ar"
                              ? plan.description_ar
                              : plan.description_en;
                          const features = plan.features
                            ? plan.features.split(",")
                            : [];

                          const priceLabel =
                            plan.prices && plan.prices[0]
                              ? plan.prices[0].price === "0.00"
                                ? t("price_0_sar")
                                : `${plan.prices[0].price} ${t("sar")}`
                              : t("price_not_available");
                          const selectedPriceObj = plan?.prices?.find(
                            (p) =>
                              parseInt(p?.duration) ===
                              parseInt(selectedDuration)
                          );
                          const price = selectedPriceObj
                            ? parseFloat(selectedPriceObj.price)
                            : 0;

                          let rawFeatures = [];
                          if (selectedDuration === 1) {
                            rawFeatures =
                              language === "en"
                                ? plan?.monthly_features_en
                                  ? plan?.monthly_features_en
                                  : []
                                : plan?.monthly_features_ar
                                ? plan?.monthly_features_ar
                                : [];
                          } else if (selectedDuration === 12) {
                            rawFeatures =
                              language === "en"
                                ? plan?.annual_features_en
                                  ? plan?.annual_features_en
                                  : []
                                : plan?.annual_features_ar
                                ? plan?.annual_features_ar
                                : [];
                          }

                          if (isFree) return;

                          return (
                            <Col lg={6} xl={6} className="mb-4" key={plan.id}>
                              <Box
                                className={`planDataContainer ${
                                  isHighlighted ? "planDataContainerBlue" : ""
                                }`}
                              >
                                <Box className="headToTxt">
                                  <SubHeadingStyled
                                    color="var(--text)"
                                    size="24px"
                                  >
                                    {t(plan.title)}
                                  </SubHeadingStyled>

                                  <Box className="descBox">
                                    <TextStyled
                                      size="15px"
                                      color="var(--text2)"
                                    >
                                      {t(plan.description)}
                                    </TextStyled>

                                    {isHighlighted && (
                                      <TextStyled
                                        size="13px"
                                        color="var(--redColor)"
                                      >
                                        {t("expiry_date")}:{" "}
                                        {
                                          user?.organization
                                            ?.latest_subscription
                                            ?.expiration_date
                                        }{" "}
                                        {/* Replace with real expiry if available */}
                                      </TextStyled>
                                    )}
                                  </Box>

                                  {isFree ? (
                                    <Box className="subsPriceCurrency">
                                      <SpanStyled
                                        className="subsPrice"
                                        size="28px"
                                        color="var(--themeColor)"
                                      >
                                        0
                                      </SpanStyled>
                                      <SpanStyled
                                        className="subsCurrency"
                                        size="16px"
                                        color="var(--themeColor)"
                                      >
                                        {user?.organization?.currency
                                          ?.stringCode || t("sar")}
                                      </SpanStyled>
                                    </Box>
                                  ) : (
                                    <Box className="subsPriceCurrency">
                                      <SpanStyled
                                        className="subsPrice"
                                        size="28px"
                                        color="var(--themeColor)"
                                      >
                                        {price}
                                      </SpanStyled>
                                      <SpanStyled
                                        className="subsCurrency"
                                        size="16px"
                                        color="var(--themeColor)"
                                      >
                                        {user?.organization?.currency
                                          ?.stringCode || t("sar")}
                                      </SpanStyled>
                                    </Box>
                                  )}

                                  <TextStyled size="14px" color="var(--text)">
                                    {/* {plan.duration} */}
                                    {isFree
                                      ? t("free_forever")
                                      : selectedDuration === 1
                                      ? t("monthly")
                                      : t("annually")}
                                  </TextStyled>
                                </Box>
                                <Box className="commonBtns w-100 p-0 mt-3">
                                  <ButtonStyled
                                    border={"var(--themeColor)"}
                                    bg={
                                      !isHighlighted
                                        ? "transparent"
                                        : "var(--themeColor)"
                                    }
                                    color={
                                      !isHighlighted
                                        ? "var(--themeColor)"
                                        : "var(--white)"
                                    }
                                    size="20px"
                                    padding="12px 20px"
                                    weight="bold"
                                    clName="w-100"
                                    className="w-100"
                                    label={buttonLabel}
                                    onClick={() =>
                                      addLicense(
                                        t(
                                          isHighlighted
                                            ? "RenewSubscriptionPlan"
                                            : "subscription_plan"
                                        ),
                                        plan
                                      )
                                    }
                                  />
                                </Box>

                                {isExpired &&
                                plan?.id ==
                                  user?.organization?.latest_subscription
                                    ?.plan_id &&
                                parseInt(user?.organization?.auto_renewal) !==
                                  1 ? (
                                  <Box className="commonBtns w-100 p-0 mt-3">
                                    <ButtonStyled
                                      border={"var(--themeColor2)"}
                                      bg={"var(--themeColor2)"}
                                      color={"var(--white)"}
                                      size="20px"
                                      padding="12px 20px"
                                      weight="bold"
                                      clName="w-100"
                                      className="w-100"
                                      label={t("request_grace_period")}
                                      onClick={() =>
                                        requestGracePeriod(plan.id)
                                      }
                                    />
                                  </Box>
                                ) : (
                                  ""
                                )}
                                {isHighlighted && (
                                  <Box className="mt-2">
                                    <Box className="d-flex justify-content-end gap-2">
                                      <SpanStyled color="var(--text)">
                                        {t("auto_renew")}
                                      </SpanStyled>
                                      <Box
                                        className={`switch ${
                                          parseInt(autoSubscibe) === 1
                                            ? "active"
                                            : ""
                                        }`}
                                        onClick={onStatusToggle}
                                        data-tooltip-id="action-tooltip"
                                        data-tooltip-content={t("status")}
                                      >
                                        <SpanStyled className="slider" />
                                      </Box>
                                    </Box>
                                  </Box>
                                )}
                                {/* {isExpired &&
                              plan?.id ==
                                user?.current_organization?.plan_detail
                                  ?.plan_id ? (
                                <Box className="commonBtns w-100 p-0 mt-3">
                                  <ButtonStyled
                                    border={"var(--themeColor2)"}
                                    bg={"var(--themeColor2)"}
                                    color={"var(--white)"}
                                    size="20px"
                                    padding="12px 20px"
                                    weight="bold"
                                    clName="w-100"
                                    className="w-100"
                                    label={t("request_grace_period")}
                                    onClick={() => requestGracePeriod(plan.id)}
                                  />
                                </Box>
                              ) : (
                                ""
                              )} */}

                                <CheckList
                                  listData={rawFeatures}
                                  // listData={
                                  //   features
                                  //     ? JSON.parse(features).map((item, i) => ({
                                  //         id: i + 1,
                                  //         listTxt: item.trim(),
                                  //       }))
                                  //     : []
                                  // }
                                  listBlue={!isFree}
                                />
                              </Box>
                            </Col>
                          );
                        })}
                      {/* <Col lg={6} xl={4} className="mb-4">
                      <Box
                        className={`planDataContainer ${
                          isCustomHighlighted ? "planDataContainerGreen" : ""
                        }`}
                      >
                        <Box className="headToTxt">
                          <SubHeadingStyled color="var(--text)" size="24px">
                            {t("EnterprisePlanCustom")}
                          </SubHeadingStyled>
                          <Box className="descBox">
                            <TextStyled size="15px" color="var(--text2)">
                              {t("CustomSolutionsComprehensive")}
                            </TextStyled>
                          </Box>
                          {isCustomHighlighted ? (
                            <TextStyled size="14px" color="var(--redColor)">
                              {t("expiry_date")}: {}{" "}
                            </TextStyled>
                          ) : (
                            <>
                              <Box className="subsContactIcon">
                                <Chat />
                              </Box>
                              <TextStyled size="14px" color="var(--text)">
                                {t("Contactsalespricing")}
                              </TextStyled>
                            </>
                          )}
                        </Box>
                        <Box className="commonBtns w-100 p-0 mt-3">
                          {isCustomHighlighted ? (
                            <ButtonStyled
                              border={"var(--themeColor)"}
                              bg={"var(--themeColor)"}
                              color={"var(--white)"}
                              size="20px"
                              padding="12px 20px"
                              weight="bold"
                              clName="w-100"
                              className="w-100"
                              label={t("RenewPlan")}
                              onClick={() =>
                                addLicense(t("RenewSubscriptionPlan"))
                              }
                            />
                          ) : (
                            <ButtonStyled
                              bg="transparent"
                              border="var(--themeColor)"
                              color="var(--themeColor)"
                              size="20px"
                              padding="12px 20px"
                              weight="bold"
                              clName="w-100"
                              className="w-100"
                              label={t("ContactSalesTeam")}
                              onClick={contactSales}
                            ></ButtonStyled>
                          )}
                        </Box>
                        {isCustomHighlighted ? (
                          <Box className="commonBtns w-100 p-0 mt-3">
                            <ButtonStyled
                              border={"var(--themeColor)"}
                              bg={"var(--themeColor)"}
                              color={"var(--white)"}
                              size="20px"
                              padding="12px 20px"
                              weight="bold"
                              clName="w-100"
                              className="w-100"
                              label={t("request_grace_period")}
                              onClick={() => requestGracePeriod(1)}
                            />
                          </Box>
                        ) : (
                          ""
                        )}
                        <CheckList
                          listData={enterprisePlanCheckList}
                          listBlue={false}
                        />
                      </Box>
                    </Col> */}
                    </Row>
                  </>
                )}
              </SubscriptionWrapper>
              <AddLicenseModal
                title={modalTitle} // Pass modal title
                show={showAddLicenseModal}
                onHide={() => {
                  //   setPlanData({
                  //     id: user?.current_organization?.plan_detail?.plan_id,
                  //     amount: user?.current_organization?.plan_detail?.price,
                  //     expired: user?.current_organization?.plan_detail?.expired,
                  //   });
                  setShowAddLicenseModal(false);
                }}
                planInfo={planData}
                action={action}
              />
              {/* <ContactSalesModal
                show={showContactSalesModal}
                onHide={() => setShowContactSalesModal(false)}
              /> */}
            </FormDataContainer>
          </Col>
        </Row>
      </Box>
    </SettingsWrapper>
  );
};

export default Billing;
